<template>
  <div
    v-if="form_open || isModalOpen"
    class="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex justify-center items-center p-4 z-50"
  >
    <div class="bg-white p-8 rounded-lg shadow-lg max-w-2xl w-full mx-auto">
      <form @submit.prevent="handleSubmit">
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-xl font-semibold text-gray-900">
            {{ mode === 'Edit' ? 'Update' : 'Create' }}
            {{ capitalize(model_name) }}
          </h3>
          <Multi_Button
            :button-char="'X'"
            :background-color="'bg-gray-200'"
            :text-color="'text-gray-600'"
            :text-size="'text-sm'"
            :hover-class="'hover:text-orange-500 transition-colors'"
            @click="
              form_open = !form_open;
              $emit('close');
            "
          />
        </div>
        <div v-for="(value, key) in form_schema.fields" :key="key" class="">
          <div class="grid grid-cols-12 gap-4 mb-4 flex items-start">
            <label class="p-1 col-span-3 text-gray-700 font-medium" :for="key">{{ value.label }}</label>
            <div class="col-span-9">
              <div v-if="value.form_type === 'input'">
                <input
                  v-if="value.input_type === 'checkbox'"
                  :id="value.name"
                  v-model="formData[value.field_name]"
                  :type="value.input_type"
                  class="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-blue-dark"
                />
                <textarea
                  v-else-if="value.input_type === 'textarea'"
                  v-model="formData[value.field_name]"
                  class="sensitive p-2 w-full h-24 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-dark focus:border-blue-dark"
                ></textarea>
                <div
                  v-else-if="value.input_type === 'readonly'"
                  class="p-2 w-full border block border-gray-300 bg-gray-50 rounded-lg shadow-sm text-gray-500"
                >
                  {{ formData[value.field_name] }}
                </div>
                <input
                  v-else
                  :id="value.name"
                  v-model="formData[value.field_name]"
                  :data-spoof-kind="value.input_type === 'date' ? 'date' : null"
                  class="sensitive p-2 w-full border block border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-dark focus:border-blue-dark"
                  :type="value.input_type"
                />
              </div>

              <div v-else-if="value.form_type === 'select'">
                <select
                  :id="key"
                  v-model="formData[value.field_name]"
                  :disabled="model === 'action' && !!object?.id && value.field_name === 'action_type'"
                  class="p-2 block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-dark focus:border-blue-dark"
                >
                  <option v-for="option in dropdown_values[key]" :key="option.id" :value="option.id">
                    {{ option.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <Field_Errors
            v-if="errors[value.field_name]"
            :key="errors"
            :received_errors="errors[value.field_name]"
          />
        </div>
        <button
          class="ml-auto btn-primary"
          type="submit"
        >
          {{ mode === 'Edit' ? 'Update' : 'Create' }}
        </button>
      </form>
    </div>
  </div>
  <button
    v-if="!hideIcon"
    class="btn-primary-sm"
    @click="form_open = !form_open"
  >
    <font-awesome-icon :icon="['fas', 'plus']" class="h-4 w-4" />
    Create
  </button>
</template>

<script>
import axios from 'axios';
import { capitalize } from 'lodash';
import Multi_Button from '@/components/widgetcomponents/Multi_Button.vue';
import Field_Errors from '@/components/widgetcomponents/Field_Errors.vue';
import { mapGetters } from 'vuex';
import { isDemoMode } from '@/utils/demoSpoof';

export default {
  components: { Field_Errors, Multi_Button },
  props: {
    object: Object, // The model data for edit
    schema: Object, // Form schema for Custom Schema
    model: String, // Model for Template Schema
    mode: String,
    isModalOpen: Boolean,
    hideIcon: Boolean,
  },
  data() {
    return {
      formData: this.object ? { ...this.object } : {},
      form_schema: {},
      model_name: '',
      form_open: false,
      readonly_values: {}, // To store display names for read-only select fields
      template_schemas: {
        action: {
          model_name: 'Action',
          api_url: '/api/graph-actions/',
          fields: {
            action_type: {
              field_name: 'action_type',
              label: 'Type',
              form_type: 'select',
              api_url: '/api/graph-action-types/',
            },
            action_name: {
              field_name: 'action_name',
              label: 'Name',
              form_type: 'input',
            },
            description: {
              field_name: 'description',
              label: 'Description',
              form_type: 'input',
              input_type: 'textarea',
            },
            notes: {
              field_name: 'notes',
              label: 'Notes',
              form_type: 'input',
              input_type: 'textarea',
            },
            due_date: {
              field_name: 'due_date',
              label: 'Due Date',
              form_type: 'input',
              input_type: 'date',
            },
            completed_date: {
              field_name: 'completed_date',
              label: 'Completed Date',
              form_type: 'input',
              input_type: 'date',
            },
          },
        },
      },
      errors: {},
      dropdown_values: {},
    };
  },
  mounted() {
    if (this.schema) {
      this.form_schema = this.schema;
    } else if (this.template_schemas[this.model]) {
      this.form_schema = this.template_schemas[this.model];
      this.model_name = this.form_schema.model_name;
    }

    // Fetch dropdown values
    for (const field_key in this.form_schema.fields) {
      const field = this.form_schema.fields[field_key];
      if (field.form_type === 'select'){
        if(field.object_id){
          this.fetchSelectedValue(field.api_url, field.field_name,field.object_id);
        }
        else{
          this.fetchDropdownValues(field.api_url, field_key, field.params);
        }
      }
    }

    if (this.model === 'action' && this.mode !== 'Edit') {
      this.formData.owner_object_id = this.current_user.id;
      this.formData.owner_content_type_model = 'appuser';
    }
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated', 'current_user', 'loading']),
  },
  methods: {
    // Demo Mode: dates tagged data-spoof-kind="date" inside this record
    // share one offset (an opaque key; nothing is bound outside Demo Mode).
    isReadOnly(field_name) {
      return this.object && this.object[field_name] !== undefined && this.object[field_name] !== null;
    },
    capitalize,
    fetchDropdownValues(api_url, field, params = null) {
      axios
        .get(api_url, { params })
        .then(response => {
          this.dropdown_values[field] = this.model === 'action' && field === 'action_type' && this.mode !== 'Edit'
            ? response.data.filter(item => item.id === 'action_item') : response.data;
        })
        .catch(error => {
          console.error(`Error loading dropdown values for ${field}:`, error);
        });
    },
    fetchSelectedValue(api_url, field, id) {
      axios
        .get(`${api_url}${id}/`)
        .then(response => {
          this.dropdown_values[field] = [response.data];
          this.formData[field] = id
        })
        .catch(error => {
          console.error(`Error fetching selected value for ${field}:`, error);
        });
    },
    transformFormData(data) {
      const transformed = {};

      for (const [key, value] of Object.entries(data)) {
        const [field_name, field_name_subkey] = key.split('__');

        if (field_name_subkey) {
          if (!transformed[field_name]) {
            transformed[field_name] = {};
          }
          transformed[field_name][field_name_subkey] = value;
        } else {
          transformed[field_name] = value;
        }
      }

      return transformed;
    },
    handleSubmit() {
      const method = this.object && this.object.id ? 'PATCH' : 'POST';
      const url =
        this.object && this.object.id
          ? `${this.form_schema.api_url}${this.object.id}/`
          : this.form_schema.api_url;

      const submitted = this.transformFormData(this.formData);
      const allowed = new Set([...Object.keys(this.form_schema.fields), 'entity', 'owner_object_id', 'owner_content_type_model']);
      const transformedData = Object.fromEntries(Object.entries(submitted).filter(([key, value]) =>
        allowed.has(key) && (!this.object?.id || value !== this.object[key])
      ));
      // Demo Mode: the record fields are read-only, so an edit with nothing
      // changed sends nothing.
      if (method === 'PATCH' && !Object.keys(transformedData).length && isDemoMode()) return;

      axios({
        method,
        url,
        data: transformedData,
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then(response => {
          this.formData.id = response.data.id;
          this.form_open = false;
          this.$emit('update');
        })
        .catch(error => {
          this.errors = {};
          if (error.response && error.response.data) {
            Object.keys(error.response.data).forEach(key => {
              this.errors[key] = error.response.data[key];
            });
          }

          console.error('Error submitting the form:', error);
        });
    },
  },
};
</script>

<style scoped>
/* Add any necessary styles here */
</style>
